import type { MembershipBase } from 'sdk';
import { appConfig, type ChannelEntityType } from 'shared';
import type { AncestorSlugs } from '~/modules/entities/types';
import { findMembership } from '~/query/enrichment/helpers';
import type { EnrichableChannel } from '~/query/enrichment/types';
import { getField } from '~/utils/get-field';
import { objectKeys } from '~/utils/object-keys';

/** entityType -> entityId -> slug */
export type SlugIndex = Map<ChannelEntityType, Map<string, string>>;

function hasAncestorSlugsChanged(a: AncestorSlugs | null, b: AncestorSlugs | null): boolean {
  if (!a && !b) return false;
  if (!a || !b) return true;
  const aKeys = objectKeys(a);
  if (aKeys.length !== Object.keys(b).length) return true;
  return aKeys.some((type) => a[type] !== b[type]);
}

/**
 * Reads ancestor ids from the caller's membership of the row, which also names its menu parent, then from the row. Reads
 * a pre-built index, falling back to the ancestor id when a slug is not indexed; rewriteUrlToSlug corrects the URL later.
 */
export function enrichWithAncestorSlugs(
  item: EnrichableChannel,
  memberships: MembershipBase[],
  ancestors: readonly ChannelEntityType[],
  slugIndex: SlugIndex,
): EnrichableChannel {
  if (ancestors.length === 0) return item;

  const membership = findMembership(memberships, item.id);
  const slugs: AncestorSlugs = {};
  let found = false;

  for (const ancestorType of ancestors) {
    const idKey = appConfig.entityIdColumnKeys[ancestorType];
    const ancestorId = getField(membership, idKey) ?? getField(item, idKey);
    if (typeof ancestorId !== 'string') continue;
    slugs[ancestorType] = slugIndex.get(ancestorType)?.get(ancestorId) ?? ancestorId;
    found = true;
  }

  if (!found) return item;
  if (!hasAncestorSlugsChanged(item.ancestorSlugs ?? null, slugs)) return item;

  return { ...item, ancestorSlugs: slugs };
}
